import * as React from 'react';

import type * as api from '@gitbook/api-client';

import { useNavigationFunctions } from '../../navigation';
import { apiQuery, apiQueryClient, useCurrentOrganizationOrNull } from '../../state';
import { CustomerIOActionHandler } from './CustomerIOActionHandler';

const SITE_QUALITY_ACTIONS = [
    { actionName: 'open-content-gaps', screenType: 'content_gaps' },
    { actionName: 'open-broken-links', screenType: 'link_audit' },
    { actionName: 'open-style-guide', screenType: 'styleguide_new' },
] as const;

/**
 * Open quality screens for an accessible site from Customer.io in-app messages.
 *
 * Temp: added for the mkt quality campaign - will be cleaned once it ends - RND-13295
 */
export function SiteQualityCustomerIOActions(): React.ReactElement | null {
    const organization = useCurrentOrganizationOrNull();
    const { navigate } = useNavigationFunctions();

    if (!organization) {
        return null;
    }

    return (
        <>
            {SITE_QUALITY_ACTIONS.map(({ actionName, screenType }) => (
                <CustomerIOActionHandler
                    key={actionName}
                    actionName={actionName}
                    onAction={async () => {
                        const organizationContent = await apiQueryClient.ensureQueryData(
                            apiQuery('getOrganizationAllContent', {
                                organizationId: organization.id,
                            })
                        );
                        const siteId = getSiteQualityActionTargetSiteId(
                            organization.defaultContent?.site,
                            organizationContent.sites
                        );
                        if (!siteId) {
                            return;
                        }

                        navigate({
                            screen: {
                                type: screenType,
                                organization: organization.id,
                                site: siteId,
                            },
                        });
                    }}
                />
            ))}
        </>
    );
}

/**
 * Select the organization default site when accessible, otherwise the oldest accessible
 * published site, then the oldest accessible site.
 */
export function getSiteQualityActionTargetSiteId(
    defaultSiteId: string | undefined,
    sites: api.OrganizationAllSite[]
): string | null {
    const accessibleSites = sites
        .map(({ site }) => site)
        .filter((site) => site.permissions.access)
        .sort(
            (a, b) =>
                Number(Boolean(b.published)) - Number(Boolean(a.published)) ||
                a.createdAt.localeCompare(b.createdAt)
        );

    return (
        accessibleSites.find((site) => site.id === defaultSiteId)?.id ??
        accessibleSites[0]?.id ??
        null
    );
}
